import {
  FileText,
  History,
  IdCard,
  KeyRound,
  Link2,
  Link2Off,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { poOdmowie401 } from '../../lib/martwaSesja';
import { ZDANIE_MARTWEJ_SESJI } from '../../lib/nexusApi';
import { NEXUS_API_URL } from '../../lib/nexusApiCore';
import { supabase } from '../../lib/supabase';

import { DiagnozaNumerow } from './DiagnozaNumerow';
import {
  kandydaciWiersza,
  komunikatWiersza,
  podsumujKadry,
  tylkoAktywni,
  wymagajaUwagi,
  zdanieOKadrach,
  type WierszKadr,
} from './kadryStan';
import {
  brakiPolaczenia,
  domyslnyOkres,
  konfiguracjaDoWyslania,
  maDiagnozeTelefonow,
  manifestWtyczki,
  maImportHistorii,
  maSynchronizacjeKadr,
  maWysylkeEwidencji,
  okresZPola,
  ostrzezenieIdentyfikatoraFirmy,
  opiszWynikFirmFk,
  opiszWynikImportuHistorii,
  opiszStanTozsamosci,
  opiszWynikSynchronizacjiFk,
  opiszWynikEwidencji,
  opiszWynikSynchronizacjiKadr,
  opiszWynikSynchronizacjiSprzedazy,
  opiszWynikUzgodnieniaKadr,
  rodzajSynchronizacji,
  sukcesImportuHistorii,
  sukcesSynchronizacjiFk,
  sukcesSynchronizacjiKadr,
  sukcesSynchronizacjiSprzedazy,
  trasaImportuHistorii,
  trasaSynchronizacji,
  TRASA_EWIDENCJI_KADR,
  TRASA_SYNCHRONIZACJI_KADR,
  TRASA_UZGODNIENIA_KADR,
  zakresyPoczatkowe,
  type FirmaZrodlaFk,
  type ManifestWtyczki,
} from './wtyczkiManifest';

/**
 * Błąd z odpowiedzi nexus-core dla trzech przycisków wtyczki. 401 jest
 * pytaniem o sesję (lib/martwaSesja.ts): 13.09.2026 pod „Synchronizuj teraz"
 * i „Importuj historię" stało gołe „Unauthorized" z tokenu, którego serwer
 * auth już nie znał — a strona pokazywała to jako błąd wtyczki.
 */
async function bladOdpowiedzi(res: Response, cialo: unknown, domyslne: string): Promise<Error> {
  if (res.status === 401 && (await poOdmowie401('nexus-core'))) {
    return new Error(ZDANIE_MARTWEJ_SESJI);
  }
  const opis = (cialo as { error?: string } | null)?.error;
  return new Error(opis || `${domyslne} (${res.status}).`);
}

// =============================================================================
// „Połącz" — krok 3 kolejności budowy z `WTYCZKI_INTEGRACYJNE.md`.
//
// Pierwszy ekran tej ścieżki, który zobaczy właściciel firmy. Do migracji 453
// wiersz `genesis_connectors` mógł powstać wyłącznie przez punkt super-admina,
// więc „podłącz swój Shopify" znaczyło „napisz do nas". Teraz idzie to trzema
// funkcjami RPC z granicą najemcy w ciele — powłoka woła je NA WPROST, kluczem
// zalogowanego użytkownika, bez pośrednictwa nexus-core.
//
// Token nigdy nie wraca z serwera. Pole jest więc puste przy każdym otwarciu,
// a jego pustka przy zapisie znaczy „nie zmieniaj", nie „skasuj" — inaczej
// poprawka literówki w adresie sklepu po cichu zrywałaby połączenie. Do
// zerwania jest osobny przycisk, który mówi o tym wprost.
// =============================================================================

/** Stan połączenia oddawany przez `genesis_konektory_najemcy` (bez sekretów). */
export interface StanKonektora {
  kind: string;
  status: string;
  config: Record<string, unknown> | null;
  has_secret: boolean;
  last_pull_at: string | null;
  last_error: string | null;
  pulled_count: number | null;
}

/** Pobierz stan integracji najemcy. Pusto, gdy brak uprawnień albo błąd. */
export async function pobierzStanKonektorow(orgId: string): Promise<StanKonektora[]> {
  const { data, error } = await supabase.rpc('genesis_konektory_najemcy', {
    p_organization_id: orgId,
  });
  if (error) {
    // Brak roli administracyjnej to normalny przypadek, nie awaria — pracownik
    // po prostu nie zobaczy sekcji połączenia.
    console.warn('Nie udało się pobrać stanu integracji:', error.message);
    return [];
  }
  return (data ?? []) as StanKonektora[];
}

function opisStanu(stan: StanKonektora | undefined): { tekst: string; klasa: string } {
  if (!stan) return { tekst: 'Niepodłączona', klasa: 'text-gray-400 bg-white/5' };
  if (stan.last_error)
    return { tekst: 'Błąd ostatniego zaciągu', klasa: 'text-red-300 bg-red-500/10' };
  if (!stan.has_secret) return { tekst: 'Bez tokenu', klasa: 'text-amber-300 bg-amber-500/10' };
  if (stan.last_pull_at) {
    return {
      tekst: `Zaciągnięto ${stan.pulled_count ?? 0} rekordów`,
      klasa: 'text-emerald-300 bg-emerald-500/10',
    };
  }
  return { tekst: 'Podłączona', klasa: 'text-emerald-300 bg-emerald-500/10' };
}

export function PolaczWtyczke({
  kind,
  orgId,
  stan,
  onZmiana,
  przedPolaczeniem,
}: {
  kind: string;
  orgId: string;
  stan: StanKonektora | undefined;
  onZmiana: () => void;
  /**
   * Wołane RAZ, tuż przed `genesis_konektor_polacz`, i tylko gdy formularz jest
   * kompletny. Rzucenie zatrzymuje połączenie i pokazuje komunikat.
   *
   * Istnieje dla aktu otwarcia (krok 7). `genesis_konektor_polacz` ma DWIE
   * bramki: rolę i subskrypcję wtyczki w marketplace (453). W panelu modułów
   * druga jest z góry spełniona — `PolaczWtyczke` renderuje się tam wyłącznie
   * dla wtyczki już włączonej. Najemca prowizoryczny z aktu otwarcia nie ma
   * ŻADNEJ subskrypcji, więc bez tego haczyka pierwsze „Połącz" w Genesis
   * odbiłoby się od bazy komunikatem „Wtyczka nie jest włączona”.
   *
   * Kolejność jest tu istotą: subskrypcja MUSI powstać przed połączeniem.
   * Odwrotnej nie da się uratować — RPC odmawia, zanim cokolwiek zapisze.
   */
  przedPolaczeniem?: () => Promise<void>;
}) {
  const manifest: ManifestWtyczki | undefined = manifestWtyczki(kind);
  const [otwarty, setOtwarty] = useState(false);
  const [config, setConfig] = useState<Record<string, string>>({});
  const [zakresy, setZakresy] = useState<string[]>([]);
  const [sekret, setSekret] = useState('');
  const [zajety, setZajety] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [potwierdzRozlaczenie, setPotwierdzRozlaczenie] = useState(false);
  const [synchronizuje, setSynchronizuje] = useState(false);
  const [wynikSync, setWynikSync] = useState<{ tekst: string; sukces: boolean } | null>(null);
  const [wykrywa, setWykrywa] = useState(false);
  const [firmyZrodla, setFirmyZrodla] = useState<FirmaZrodlaFk[] | null>(null);
  const [bladFirm, setBladFirm] = useState<string | null>(null);
  /** Identyfikator wybrany z listy „Wykryj firmy" — przeżywa re-seed formularza. */
  const [firmaWykryta, setFirmaWykryta] = useState<string | null>(null);
  /** Sekcja „Kadry": mapa tożsamości z RLS, nazwiska z kartoteki, stan przycisku. */
  const [wierszeKadr, setWierszeKadr] = useState<WierszKadr[] | null>(null);
  const [imionaPracownikow, setImionaPracownikow] = useState<Map<string, string>>(new Map());
  const [liczbaPracownikow, setLiczbaPracownikow] = useState(0);
  const [synchronizujeKadry, setSynchronizujeKadry] = useState(false);
  const [wynikKadr, setWynikKadr] = useState<{ tekst: string; sukces: boolean } | null>(null);
  /** `worker_id`, dla którego trwa wskazanie kandydata — blokuje podwójny klik. */
  const [uzgadniany, setUzgadniany] = useState<string | null>(null);
  /** Licznik wymuszający ponowny odczyt sekcji po przebiegu i po uzgodnieniu. */
  const [odswiezenieKadr, setOdswiezenieKadr] = useState(0);
  const [miesiacEwidencji, setMiesiacEwidencji] = useState<string>(() => domyslnyOkres());
  const [wysylaEwidencje, setWysylaEwidencje] = useState(false);
  const [wynikEwidencji, setWynikEwidencji] = useState<{ tekst: string; sukces: boolean } | null>(
    null
  );
  /** Import historii: miesiąc (`RRRR-MM`), stan i ostatni wynik. */
  const [miesiacHistorii, setMiesiacHistorii] = useState<string>(() => domyslnyOkres());
  const [importuje, setImportuje] = useState(false);
  const [wynikHistorii, setWynikHistorii] = useState<{ tekst: string; sukces: boolean } | null>(
    null
  );

  // Konfiguracja wraca z serwera (jest jawna), token nie. Przy każdym otwarciu
  // formularz startuje z zapisanym adresem i PUSTYM tokenem.
  useEffect(() => {
    const seed: Record<string, string> = {};
    for (const f of manifest?.configFields ?? []) {
      const v = stan?.config?.[f.key];
      seed[f.key] = typeof v === 'string' ? v : v == null ? '' : String(v);
    }
    // Wybór z listy „Wykryj firmy" ma przetrwać re-seed przy otwarciu formularza
    // — inaczej kliknięty identyfikator znikałby, zanim najemca go zobaczy.
    if (firmaWykryta) seed['firma'] = firmaWykryta;
    setConfig(seed);
    if (manifest) setZakresy(zakresyPoczatkowe(manifest, stan?.config));
    setSekret('');
    setBlad(null);
  }, [stan, manifest, otwarty, firmaWykryta]);

  // Sekcja „Kadry" czyta mapę tożsamości WPROST przez RLS — tabela z 711 nie
  // niesie danych osobowych, a jej polityka SELECT przepuszcza aktywnego
  // członka organizacji. Nazwiska dokładamy z kartoteki, czyli z miejsca,
  // które ten sam człowiek i tak widzi. Osobna trasa odczytu w nexus-core
  // byłaby powiększeniem powierzchni API po to, żeby oddać to samo.
  const kadryWidoczne = Boolean(manifest && maSynchronizacjeKadr(manifest, stan?.config));
  useEffect(() => {
    if (!kadryWidoczne) {
      setWierszeKadr(null);
      return;
    }
    let aktualne = true;
    void (async () => {
      const [mapa, ludzie] = await Promise.all([
        supabase
          .from('shop_floor_worker_external_ids')
          .select('worker_id,external_id,acronym,last_status,last_error,synced_at,updated_at')
          .eq('organization_id', orgId)
          .eq('system', kind)
          // Tożsamość jest tożsamością W FIRMIE (713): wiersze sprzed zmiany
          // `firma` dotyczą innej spółki biura i nie opisują tego połączenia.
          .eq('company', typeof stan?.config?.firma === 'string' ? stan.config.firma.trim() : ''),
        supabase
          .from('shop_floor_workers')
          .select('id,first_name,last_name')
          .eq('organization_id', orgId)
          .eq('is_active', true),
      ]);
      if (!aktualne) return;
      if (mapa.error) {
        // Brak uprawnień albo awaria odczytu to nie jest „zero pracowników" —
        // sekcja zostaje pusta zamiast pokazywać wyzerowane liczniki.
        console.warn('Nie udało się odczytać stanu kadr:', mapa.error.message);
        setWierszeKadr(null);
        return;
      }
      const osoby = (ludzie.data ?? []) as {
        id: string;
        first_name: string | null;
        last_name: string | null;
      }[];
      setImionaPracownikow(
        new Map(osoby.map((o) => [o.id, `${o.first_name ?? ''} ${o.last_name ?? ''}`.trim()]))
      );
      setLiczbaPracownikow(osoby.length);
      setWierszeKadr((mapa.data ?? []) as WierszKadr[]);
    })();
    return () => {
      aktualne = false;
    };
  }, [kadryWidoczne, orgId, kind, stan?.config?.firma, odswiezenieKadr]);

  // Powłoka nie zna tej wtyczki — nie zgadujemy, o co zapytać. To znaczy, że
  // katalog wyprzedził manifest i naprawa należy do `wtyczkiManifest.ts`.
  if (!manifest) {
    return (
      <p className="w-full text-center px-4 py-2.5 rounded-ids-md text-sm text-gray-400 bg-white/5">
        Ta integracja nie ma jeszcze formularza połączenia.
      </p>
    );
  }

  const polaczona = Boolean(stan);
  const { tekst, klasa } = opisStanu(stan);

  const polacz = async () => {
    const braki = brakiPolaczenia(manifest, config, sekret, Boolean(stan?.has_secret));
    if (braki.length > 0) {
      setBlad(`Uzupełnij: ${braki.join(', ')}.`);
      return;
    }
    setZajety(true);
    setBlad(null);
    try {
      // Dopiero TU, nie przy zaznaczeniu karty. Zaznaczenie „używamy tego" nie
      // może uruchamiać zegara trialu wtyczki, której najemca ostatecznie nie
      // podłączył — a rozmyślenie się po zaznaczeniu jest zwykłym ruchem.
      if (przedPolaczeniem) await przedPolaczeniem();

      const { error } = await supabase.rpc('genesis_konektor_polacz', {
        p_organization_id: orgId,
        p_kind: kind,
        // Zakresy jadą w tym samym jawnym configu, co adres/konto — to jest
        // decyzja najemcy „co ta wtyczka ma u nas robić", nie sekret.
        p_config: {
          ...konfiguracjaDoWyslania(manifest, config),
          ...(manifest.zakresy?.length ? { zakresy } : {}),
        },
        // `null` znaczy „nie ruszaj sekretu" — patrz nagłówek pliku.
        p_sekret: sekret.trim() ? sekret.trim() : null,
      });
      if (error) throw new Error(error.message);
      setSekret('');
      setOtwarty(false);
      // Zapisane — od teraz prawdą o polu „Firma" jest config z serwera.
      setFirmaWykryta(null);
      onZmiana();
    } catch (err) {
      setBlad((err as Error).message || 'Nie udało się połączyć.');
    } finally {
      setZajety(false);
    }
  };

  // Rodzaj synchronizatora za przyciskami: FK (Saldeo → Provisio) albo
  // SPRZEDAŻ (GoPOS → sales_*). Trasa i zdanie wyniku zależą od rodzaju;
  // formularz jest jeden.
  const rodzaj = manifest ? rodzajSynchronizacji(manifest) : null;

  // Ręczny przebieg synchronizatora (endpoint woła ProvisioFkSync albo
  // SprzedazSync w nexus-core kluczem zalogowanego admina organizacji). Wynik —
  // liczniki albo powód pominięcia — pokazujemy pod przyciskiem; sekrety nie kursują.
  const synchronizujTeraz = async () => {
    if (!rodzaj) return;
    setSynchronizuje(true);
    setWynikSync(null);
    try {
      const { data: sesja } = await supabase.auth.getSession();
      const token = sesja.session?.access_token;
      if (!token) throw new Error('Sesja wygasła — zaloguj się ponownie.');
      const res = await fetch(`${NEXUS_API_URL}${trasaSynchronizacji(rodzaj)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organization_id: orgId, kind }),
      });
      const cialo: unknown = await res.json().catch(() => null);
      if (!res.ok) throw await bladOdpowiedzi(res, cialo, 'Synchronizacja nie powiodła się');
      setWynikSync({
        tekst:
          rodzaj === 'fk'
            ? opiszWynikSynchronizacjiFk(cialo)
            : opiszWynikSynchronizacjiSprzedazy(cialo),
        // Nie flaga `success` z serwera: `ok` z błędami źródła (np. zły
        // identyfikator firmy) ma się pokazać na bursztynowo, nie na zielono.
        sukces:
          rodzaj === 'fk' ? sukcesSynchronizacjiFk(cialo) : sukcesSynchronizacjiSprzedazy(cialo),
      });
      onZmiana();
    } catch (err) {
      setWynikSync({
        tekst: (err as Error).message || 'Synchronizacja nie powiodła się.',
        sukces: false,
      });
    } finally {
      setSynchronizuje(false);
    }
  };

  // „Wykryj firmy" — lista firm konta w źródle z identyfikatorami do pola
  // „Firma" (Saldeo: COMPANY_PROGRAM_ID). Dla najemcy, który dostaje błąd 6201
  // i nie wie, co wpisać: zamiast zgadywać, wybiera z listy własnego konta.
  const wykryjFirmy = async () => {
    setWykrywa(true);
    setBladFirm(null);
    setFirmyZrodla(null);
    try {
      const { data: sesja } = await supabase.auth.getSession();
      const token = sesja.session?.access_token;
      if (!token) throw new Error('Sesja wygasła — zaloguj się ponownie.');
      const res = await fetch(`${NEXUS_API_URL}/api/nexus/genesis/connectors/fk-firmy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organization_id: orgId, kind }),
      });
      const cialo: unknown = await res.json().catch(() => null);
      if (!res.ok) throw await bladOdpowiedzi(res, cialo, 'Nie udało się pobrać listy firm');
      const wynik = opiszWynikFirmFk(cialo);
      if ('blad' in wynik) setBladFirm(wynik.blad);
      else setFirmyZrodla(wynik.firmy);
    } catch (err) {
      setBladFirm((err as Error).message || 'Nie udało się pobrać listy firm.');
    } finally {
      setWykrywa(false);
    }
  };

  // „Importuj historię" — dokumenty z JEDNEGO wybranego miesiąca. Sync
  // cykliczny widzi tylko okno przyrostowe źródła (Saldeo: dodane w ostatnich
  // 10 dniach), więc archiwum sprzed podłączenia trzeba dociągnąć jawnie,
  // miesiąc po miesiącu — limit API źródła (20 żądań/min) nie pozwala na
  // „wszystko naraz", a najemca widzi, który miesiąc już ma.
  const importujHistorie = async () => {
    if (!rodzaj) return;
    const okres = okresZPola(miesiacHistorii);
    if (!okres) {
      setWynikHistorii({ tekst: 'Wybierz poprawny miesiąc.', sukces: false });
      return;
    }
    setImportuje(true);
    setWynikHistorii(null);
    try {
      const { data: sesja } = await supabase.auth.getSession();
      const token = sesja.session?.access_token;
      if (!token) throw new Error('Sesja wygasła — zaloguj się ponownie.');
      const res = await fetch(`${NEXUS_API_URL}${trasaImportuHistorii(rodzaj)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          organization_id: orgId,
          kind,
          rok: okres.rok,
          miesiac: okres.miesiac,
        }),
      });
      const cialo: unknown = await res.json().catch(() => null);
      if (!res.ok) throw await bladOdpowiedzi(res, cialo, 'Import historii nie powiódł się');
      setWynikHistorii({
        tekst:
          rodzaj === 'fk'
            ? opiszWynikImportuHistorii(cialo)
            : opiszWynikSynchronizacjiSprzedazy(cialo),
        sukces:
          rodzaj === 'fk' ? sukcesImportuHistorii(cialo) : sukcesSynchronizacjiSprzedazy(cialo),
      });
      onZmiana();
    } catch (err) {
      setWynikHistorii({
        tekst: (err as Error).message || 'Import historii nie powiódł się.',
        sukces: false,
      });
    } finally {
      setImportuje(false);
    }
  };

  // „Synchronizuj kartotekę teraz" — przebieg KadrySync dla jednego połączenia.
  // Klik różni się od przebiegu cyklicznego jedną rzeczą: ponawia także wiersz,
  // który Saldeo odrzuciło, bez czekania na zmianę danych u nas. To jest jego
  // sens — administrator zwolnił akronim po TAMTEJ stronie i chce spróbować.
  const synchronizujKadry = async () => {
    setSynchronizujeKadry(true);
    setWynikKadr(null);
    try {
      const { data: sesja } = await supabase.auth.getSession();
      const token = sesja.session?.access_token;
      if (!token) throw new Error('Sesja wygasła — zaloguj się ponownie.');
      const res = await fetch(`${NEXUS_API_URL}${TRASA_SYNCHRONIZACJI_KADR}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organization_id: orgId, kind }),
      });
      const cialo: unknown = await res.json().catch(() => null);
      if (!res.ok)
        throw await bladOdpowiedzi(res, cialo, 'Synchronizacja kartoteki nie powiodła się');
      setWynikKadr({
        tekst: opiszWynikSynchronizacjiKadr(cialo),
        sukces: sukcesSynchronizacjiKadr(cialo),
      });
      setOdswiezenieKadr((n) => n + 1);
    } catch (err) {
      setWynikKadr({
        tekst: (err as Error).message || 'Synchronizacja kartoteki nie powiodła się.',
        sukces: false,
      });
    } finally {
      setSynchronizujeKadry(false);
    }
  };

  // „Wyślij ewidencję za miesiąc" — ten sam przebieg, co automat, tylko
  // z ręki. Dwie różnice, obie celowe: pomija bramkę „dzień ≥ 3" i pomija
  // blokadę po odrzuceniu przez źródło, bo to jest DECYZJA człowieka, która
  // tę blokadę zdejmuje. Miesiąc już wysłany jedzie jako KOREKTA — nowa
  // wersja dokumentu; korekta identyczna z oryginałem jest odmawiana po
  // odcisku treści, więc przycisk nie ma jak zaśmiecić akt pracowniczych.
  const wyslijEwidencje = async () => {
    const okres = okresZPola(miesiacEwidencji);
    if (!okres) return;
    setWysylaEwidencje(true);
    setWynikEwidencji(null);
    try {
      const { data: sesja } = await supabase.auth.getSession();
      const token = sesja.session?.access_token;
      if (!token) throw new Error('Sesja wygasła — zaloguj się ponownie.');
      const res = await fetch(`${NEXUS_API_URL}${TRASA_EWIDENCJI_KADR}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organization_id: orgId, kind, ...okres }),
      });
      const cialo: unknown = await res.json().catch(() => null);
      if (!res.ok) throw await bladOdpowiedzi(res, cialo, 'Wysyłka ewidencji nie powiodła się');
      setWynikEwidencji({
        tekst: opiszWynikEwidencji(cialo),
        sukces: (cialo as { wynik?: string } | null)?.wynik === 'ok',
      });
      setOdswiezenieKadr((n) => n + 1);
    } catch (err) {
      setWynikEwidencji({
        tekst: (err as Error).message || 'Wysyłka ewidencji nie powiodła się.',
        sukces: false,
      });
    } finally {
      setWysylaEwidencje(false);
    }
  };

  // Wskazanie kandydata przy AMBIGUOUS. Wysyłka NIE dzieje się tu — trasa
  // zapisuje tożsamość, a dane pojadą przy najbliższym przebiegu. Rozdzielenie
  // jest celowe: jedno kliknięcie ma robić jedną rzecz, a wysyłka ma zostać
  // tam, gdzie liczy się hash i limity API źródła.
  const uzgodnijTozsamosc = async (workerId: string, externalId: string) => {
    setUzgadniany(workerId);
    setWynikKadr(null);
    try {
      const { data: sesja } = await supabase.auth.getSession();
      const token = sesja.session?.access_token;
      if (!token) throw new Error('Sesja wygasła — zaloguj się ponownie.');
      const res = await fetch(`${NEXUS_API_URL}${TRASA_UZGODNIENIA_KADR}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          organization_id: orgId,
          kind,
          worker_id: workerId,
          external_id: externalId,
        }),
      });
      const cialo: unknown = await res.json().catch(() => null);
      // 400 z tej trasy niesie WŁASNY wariant („nie jest kandydatem"), więc
      // czytamy ciało zamiast zamieniać je na gołe „400".
      if (!res.ok && res.status !== 400) {
        throw await bladOdpowiedzi(res, cialo, 'Uzgodnienie nie powiodło się');
      }
      setWynikKadr({
        tekst: opiszWynikUzgodnieniaKadr(cialo),
        sukces: res.ok,
      });
      setOdswiezenieKadr((n) => n + 1);
    } catch (err) {
      setWynikKadr({
        tekst: (err as Error).message || 'Uzgodnienie nie powiodło się.',
        sukces: false,
      });
    } finally {
      setUzgadniany(null);
    }
  };

  // Klik w firmę z listy: identyfikator ląduje w polu „Firma" formularza —
  // otwartego od razu, żeby najemca widział, co zapisuje, i mógł to zatwierdzić.
  const uzyjFirmy = (id: string) => {
    setFirmaWykryta(id);
    setOtwarty(true);
    setFirmyZrodla(null);
  };

  const rozlacz = async () => {
    setZajety(true);
    setBlad(null);
    try {
      const { error } = await supabase.rpc('genesis_konektor_rozlacz', {
        p_organization_id: orgId,
        p_kind: kind,
      });
      if (error) throw new Error(error.message);
      setPotwierdzRozlaczenie(false);
      setOtwarty(false);
      onZmiana();
    } catch (err) {
      setBlad((err as Error).message || 'Nie udało się rozłączyć.');
    } finally {
      setZajety(false);
    }
  };

  // Liczymy wyłącznie po AKTYWNEJ kartotece — i licznik, i listę do
  // przejrzenia. Wiersz dezaktywowanego pracownika zostaje w bazie (tamta
  // strona ma go z `INACTIVE`), ale nie jest już niczyją robotą.
  const wierszeAktywnych = wierszeKadr
    ? tylkoAktywni(wierszeKadr, new Set(imionaPracownikow.keys()))
    : null;
  const podsumowanieKadr = wierszeAktywnych
    ? podsumujKadry(wierszeAktywnych, liczbaPracownikow)
    : null;
  const kadryDoUwagi = wierszeAktywnych ? wymagajaUwagi(wierszeAktywnych) : [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs px-2 py-1 rounded ${klasa}`}>{tekst}</span>
        <button
          onClick={() => setOtwarty((o) => !o)}
          className="text-sm px-3 py-1.5 rounded-ids-md bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 transition-colors flex items-center gap-1.5"
        >
          {polaczona ? <RefreshCw className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          {polaczona ? 'Zmień' : 'Połącz'}
        </button>
      </div>

      {stan?.last_error && <p className="text-xs text-red-400/80 break-words">{stan.last_error}</p>}

      {/* „Synchronizuj teraz" — wtyczki z synchronizatorem (FK co godzinę,
          sprzedaż co kwadrans) i z tokenem w sejfie. Przycisk służy pierwszemu
          startowi i niecierpliwym. */}
      {polaczona && stan?.has_secret && rodzaj !== null && (
        <div className="space-y-1">
          <button
            onClick={synchronizujTeraz}
            disabled={synchronizuje}
            className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-ids-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50 text-sm transition-colors"
          >
            {synchronizuje ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            {synchronizuje ? 'Synchronizuję…' : 'Synchronizuj teraz'}
          </button>
          {wynikSync && (
            <p
              className={`text-xs break-words ${wynikSync.sukces ? 'text-emerald-300/90' : 'text-amber-300/90'}`}
            >
              {wynikSync.tekst}
            </p>
          )}

          {/* „Importuj historię" — jeden miesiąc na klik. Widoczny wyłącznie,
              gdy wtyczka umie listować po okresie I zakres „dokumenty" jest
              włączony; inaczej synchronizator odmówi i przycisk uczyłby klikać
              na darmo. */}
          {maImportHistorii(manifest, stan?.config) && (
            <div className="space-y-1">
              <div className="flex gap-2">
                <input
                  type="month"
                  value={miesiacHistorii}
                  min="2000-01"
                  max="2100-12"
                  onChange={(e) => setMiesiacHistorii(e.target.value)}
                  aria-label="Miesiąc importu historii"
                  className="px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-ids-md text-sm text-aurora-text focus:outline-none focus:border-indigo-500 font-mono"
                />
                <button
                  onClick={importujHistorie}
                  disabled={importuje || okresZPola(miesiacHistorii) === null}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-ids-md bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-50 text-sm transition-colors"
                >
                  {importuje ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <History className="w-4 h-4" />
                  )}
                  {importuje ? 'Importuję miesiąc…' : 'Importuj historię za miesiąc'}
                </button>
              </div>
              {wynikHistorii && (
                <p
                  className={`text-xs break-words ${wynikHistorii.sukces ? 'text-emerald-300/90' : 'text-amber-300/90'}`}
                >
                  {wynikHistorii.tekst}
                </p>
              )}
            </div>
          )}

          {/* „Wykryj identyfikator firmy" — dla wtyczek z polem `firma`: lista
              firm konta w źródle, klik wypełnia pole formularza. Ratunek na
              błąd 6201 (zły COMPANY_PROGRAM_ID) bez zgadywania. */}
          {manifest.configFields.some((f) => f.key === 'firma') && (
            <>
              <button
                onClick={wykryjFirmy}
                disabled={wykrywa}
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-ids-md bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-50 text-sm transition-colors"
              >
                {wykrywa ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                {wykrywa ? 'Pobieram listę firm…' : 'Wykryj identyfikator firmy'}
              </button>
              {bladFirm && <p className="text-xs text-amber-300/90 break-words">{bladFirm}</p>}
              {firmyZrodla && (
                <div className="space-y-1">
                  <p className="text-xs text-gray-400">
                    Firmy na Twoim koncie — kliknij, aby użyć identyfikatora:
                  </p>
                  {firmyZrodla.map((f, i) =>
                    f.id ? (
                      <button
                        key={f.id}
                        onClick={() => uzyjFirmy(f.id as string)}
                        className="w-full text-left px-3 py-1.5 rounded-ids-md bg-white/5 hover:bg-indigo-500/15 text-xs transition-colors"
                      >
                        <span className="font-mono text-indigo-300">{f.id}</span>
                        {f.nazwa && <span className="text-gray-300"> — {f.nazwa}</span>}
                      </button>
                    ) : (
                      <p key={`bez-id-${i}`} className="px-3 py-1.5 text-xs text-amber-300/90">
                        {f.nazwa ?? 'Firma bez nazwy'} — bez identyfikatora w programie (nadaje go
                        company/synchronize po stronie biura rachunkowego).
                      </p>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Sekcja „Diagnoza numerów" — program lojalnościowy (GoCRM): kontakty,
          które numer klientki znajduje w pięciu zapisach. Tylko odczyt. */}
      {polaczona && stan?.has_secret && maDiagnozeTelefonow(manifest) && (
        <DiagnozaNumerow orgId={orgId} />
      )}

      {/* Sekcja „Kadry" — osobna od „Synchronizuj teraz", bo odpowiada na inne
          pytanie: tamten przycisk PRZYNOSI dane, ten je WYSYŁA. Widoczna
          dopiero po włączeniu zakresu, bo przed nim nie ma czego pokazywać. */}
      {polaczona && stan?.has_secret && kadryWidoczne && (
        <div className="space-y-1.5 pt-2 border-t border-white/10">
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            Kadry
          </p>
          {podsumowanieKadr && (
            <p className="text-xs text-gray-300">{zdanieOKadrach(podsumowanieKadr)}</p>
          )}
          <button
            onClick={synchronizujKadry}
            disabled={synchronizujeKadry}
            className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-ids-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50 text-sm transition-colors"
          >
            {synchronizujeKadry ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Users className="w-4 h-4" />
            )}
            {synchronizujeKadry ? 'Wysyłam kartotekę…' : 'Synchronizuj kartotekę teraz'}
          </button>
          {wynikKadr && (
            <p
              className={`text-xs break-words ${wynikKadr.sukces ? 'text-emerald-300/90' : 'text-amber-300/90'}`}
            >
              {wynikKadr.tekst}
            </p>
          )}
          {/* „Wyślij ewidencję za miesiąc" — widoczny wyłącznie, gdy zakres
              „ewidencja" JEST włączony i kartoteka też. Bez kartoteki trasa
              odmawia za każdym razem, a przycisk, który zawsze odmawia, uczy
              ludzi, że komunikaty tej karty nic nie znaczą. */}
          {maWysylkeEwidencji(manifest, stan?.config) && (
            <div className="space-y-1 pt-1">
              <div className="flex gap-2">
                <input
                  type="month"
                  value={miesiacEwidencji}
                  min="2000-01"
                  max="2100-12"
                  onChange={(e) => setMiesiacEwidencji(e.target.value)}
                  aria-label="Miesiąc ewidencji czasu pracy"
                  className="px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-ids-md text-sm text-aurora-text focus:outline-none focus:border-indigo-500 font-mono"
                />
                <button
                  onClick={wyslijEwidencje}
                  disabled={wysylaEwidencje || okresZPola(miesiacEwidencji) === null}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-ids-md bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-50 text-sm transition-colors"
                >
                  {wysylaEwidencje ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  {wysylaEwidencje ? 'Wysyłam ewidencję…' : 'Wyślij ewidencję za miesiąc'}
                </button>
              </div>
              {/* Zdanie o korekcie stoi TUTAJ, a nie w komunikacie po kliknięciu:
                  o tym, że drugi raz znaczy nowy dokument w aktach, trzeba
                  wiedzieć PRZED kliknięciem. Saldeo nie umie podmienić pliku. */}
              <p className="text-[11px] text-gray-500">
                Miesiąc wysłany wcześniej pojedzie jako korekta — nowy dokument obok poprzedniego.
                Dokument o niezmienionej treści nie jedzie wcale.
              </p>
              {wynikEwidencji && (
                <p
                  className={`text-xs break-words ${wynikEwidencji.sukces ? 'text-emerald-300/90' : 'text-amber-300/90'}`}
                >
                  {wynikEwidencji.tekst}
                </p>
              )}
            </div>
          )}
          {kadryDoUwagi.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-gray-400">Do przejrzenia:</p>
              {kadryDoUwagi.map((w) => (
                <div
                  key={w.worker_id}
                  className="px-3 py-1.5 rounded-ids-md bg-white/5 text-xs space-y-1"
                >
                  <p className="text-gray-200">
                    {imionaPracownikow.get(w.worker_id) ?? 'Pracownik spoza aktywnej kartoteki'}
                    <span className="font-mono text-gray-500"> · {w.acronym}</span>
                  </p>
                  <p className="text-amber-300/90">{opiszStanTozsamosci(w.last_status)}</p>
                  {komunikatWiersza(w) && (
                    <p className="text-gray-500 break-words">{komunikatWiersza(w)}</p>
                  )}
                  {/* Kandydaci to SAME identyfikatory Saldeo — mapa tożsamości
                      nie przechowuje nazwisk osób spoza Universe. Który to
                      który, rozstrzyga się w kartotece Saldeo. */}
                  {kandydaciWiersza(w).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {kandydaciWiersza(w).map((id) => (
                        <button
                          key={id}
                          onClick={() => uzgodnijTozsamosc(w.worker_id, id)}
                          disabled={uzgadniany === w.worker_id}
                          className="flex items-center gap-1 px-2 py-1 rounded-ids-md bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 disabled:opacity-50 font-mono transition-colors"
                        >
                          <IdCard className="w-3 h-3" />
                          {id}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {otwarty && (
        <div className="p-3 rounded-ids-md bg-gray-900/60 border border-gray-700 space-y-3">
          {manifest.configFields.map((f) => (
            <div key={f.key}>
              <label className="text-xs text-gray-300">
                {f.label}
                {f.required && <span className="text-red-400"> *</span>}
              </label>
              <input
                type="text"
                value={config[f.key] ?? ''}
                onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="mt-1 w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-ids-md text-sm text-aurora-text placeholder-gray-600 focus:outline-none focus:border-indigo-500 font-mono"
              />
              {f.help && <p className="text-[11px] text-gray-500 mt-1">{f.help}</p>}
              {/* Pole na identyfikator firmy przyjmuje wartość, której konektor
                  nie zdoła użyć (spacje, ogonki) — bez tego zdania jedynym
                  sygnałem byłby brak jakiegokolwiek importu. */}
              {f.key === 'firma' &&
                (() => {
                  const ostrzezenie = ostrzezenieIdentyfikatoraFirmy(config[f.key] ?? '');
                  return ostrzezenie ? (
                    <p className="text-[11px] text-amber-300/90 mt-1">{ostrzezenie}</p>
                  ) : null;
                })()}
            </div>
          ))}

          {(manifest.zakresy?.length ?? 0) > 0 && (
            <div>
              <p className="text-xs text-gray-300">Zakres integracji</p>
              <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
                Włącz osobno, co ta wtyczka ma robić w systemie. Zgoda na jedno nie jest zgodą na
                wszystko.
              </p>
              <div className="space-y-1.5">
                {manifest.zakresy!.map((z) => (
                  <label
                    key={z.id}
                    className="flex items-start gap-2 p-2 rounded-ids-md bg-gray-950 border border-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={zakresy.includes(z.id)}
                      onChange={(e) =>
                        setZakresy((obecne) =>
                          e.target.checked ? [...obecne, z.id] : obecne.filter((id) => id !== z.id)
                        )
                      }
                      className="mt-0.5 accent-indigo-500"
                    />
                    <span>
                      <span className="text-xs text-aurora-text flex items-center gap-1.5">
                        {z.nazwa}
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">
                          {z.moduly.join(' · ')}
                        </span>
                      </span>
                      <span className="block text-[11px] text-gray-500">{z.opis}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {manifest.requiresSecret && (
            <div>
              <label className="text-xs text-gray-300 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                {manifest.secretLabel}
                {!stan?.has_secret && <span className="text-red-400">*</span>}
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={sekret}
                onChange={(e) => setSekret(e.target.value)}
                placeholder={
                  stan?.has_secret ? '•••••••• (zostaw puste, by nie zmieniać)' : 'Wklej token'
                }
                className="mt-1 w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-ids-md text-sm text-aurora-text placeholder-gray-600 focus:outline-none focus:border-indigo-500 font-mono"
              />
              <p className="text-[11px] text-amber-300/70 mt-1 flex items-start gap-1.5">
                <Lock className="w-3 h-3 flex-shrink-0 mt-0.5" />
                {manifest.secretHelp} Token trafia zaszyfrowany do sejfu i nigdy stamtąd nie wraca.
              </p>
            </div>
          )}

          {blad && <p className="text-xs text-red-400">{blad}</p>}

          <div className="flex gap-2">
            <button
              onClick={polacz}
              disabled={zajety}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-ids-md bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 disabled:opacity-50 text-sm transition-colors"
            >
              {zajety ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4" />
              )}
              Zapisz połączenie
            </button>
            {polaczona && (
              <button
                onClick={() => setPotwierdzRozlaczenie(true)}
                disabled={zajety}
                className="px-3 py-2 rounded-ids-md text-orange-300 hover:bg-orange-500/10 disabled:opacity-50 text-sm transition-colors flex items-center gap-1.5"
              >
                <Link2Off className="w-4 h-4" />
                Rozłącz
              </button>
            )}
          </div>

          {potwierdzRozlaczenie && (
            <div className="p-3 rounded-ids-md bg-orange-500/10 border border-orange-500/30 space-y-2">
              {/* Mówimy wprost, co ginie: token jest kasowany z sejfu, a nie
                  odkładany na bok. Ponowne podłączenie wymaga nowego. */}
              <p className="text-xs text-orange-200">
                Rozłączenie usuwa token z sejfu bezpowrotnie. Dane już zaciągnięte do grafu zostają;
                ponowne podłączenie będzie wymagało nowego tokenu.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={rozlacz}
                  disabled={zajety}
                  className="px-3 py-1.5 rounded-ids-md bg-orange-500/25 text-orange-200 hover:bg-orange-500/35 disabled:opacity-50 text-xs"
                >
                  Tak, rozłącz
                </button>
                <button
                  onClick={() => setPotwierdzRozlaczenie(false)}
                  className="px-3 py-1.5 rounded-ids-md text-gray-400 hover:bg-white/5 text-xs"
                >
                  Anuluj
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
